import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { TRANSLATION_KEY, BILLING_PERIOD, SUBSCRIPTION_STATUS } from '../constants';
import { formatPeriod, getVisibleComparisonRows } from '../utils';
import { formatCurrencyAmount } from '../currency';
import PricingToggle from './PricingToggle';
import PricingCard from './PricingCard';
import PricingTable from './PricingTable';

function hasMultipleVariants(tariffs) {
  return tariffs.some((t) => t.variants?.length > 1);
}

// Subscriptions in these statuses no longer own a plan, so nothing should be marked as current.
const NO_CURRENT_PLAN_STATUSES = new Set([
  SUBSCRIPTION_STATUS.ENDED,
  SUBSCRIPTION_STATUS.CANCELED,
  SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED,
]);

function adaptTariffToPlan(tariff, variant, isCurrentPlan, currency) {
  return {
    id: tariff.id,
    name: tariff.name,
    description: tariff.description,
    price: variant
      ? formatCurrencyAmount(variant.amount, currency)
      : formatCurrencyAmount(tariff.priceFrom, currency),
    period: variant ? formatPeriod(variant) : '',
    highlighted: isCurrentPlan,
    isPrivate: Boolean(tariff.isPrivate),
    isCustom: Boolean(tariff.isCustom),
    comparisonValues: tariff.comparisonValues ?? {},
    variant,
  };
}

const PricingPlans = ({
  tariffs = [],
  tariffMeta = { rows: [] },
  currency,
  currentPlanId,
  currentVariantId,
  subscriptionStatus,
  onSelectPlan,
  // TODO: uncomment when downgrade feature is enabled
  // onDowngrade,
  onLetsTalk,
}) => {
  const { t } = useTranslation(TRANSLATION_KEY);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const hasCurrentPlan = Boolean(currentPlanId) && !NO_CURRENT_PLAN_STATUSES.has(subscriptionStatus);

  // Open on the period the client is already on, until they pick another one.
  const currentPeriod = useMemo(
    () =>
      tariffs
        .find((tariff) => tariff.id === currentPlanId)
        ?.variants?.find((variant) => variant.id === currentVariantId)?.billingPeriodUnit,
    [tariffs, currentPlanId, currentVariantId]
  );
  const billingPeriod = selectedPeriod ?? currentPeriod ?? BILLING_PERIOD.MONTHLY;

  const showToggle = useMemo(() => hasMultipleVariants(tariffs), [tariffs]);

  const plans = useMemo(() => {
    return tariffs.map((tariff) => {
      const variant =
        tariff.variants?.length > 1
          ? tariff.variants.find((v) => v.billingPeriodUnit === billingPeriod) ?? tariff.variants[0]
          : tariff.variants?.[0] ?? null;

      return adaptTariffToPlan(tariff, variant, hasCurrentPlan && tariff.id === currentPlanId, currency);
    });
  }, [tariffs, billingPeriod, hasCurrentPlan, currentPlanId, currency]);

  const cardLabels = useMemo(
    () => ({
      from: t('FROM'),
      custom: t('CUSTOM'),
      letsTalk: t('LETS_TALK'),
      currentPlan: t('CURRENT_PLAN'),
      preparedForYou: t('PREPARED_FOR_YOU'),
    }),
    [t]
  );

  const comparisonRows = useMemo(
    () => getVisibleComparisonRows(tariffMeta?.rows, billingPeriod),
    [tariffMeta?.rows, billingPeriod]
  );

  return (
    <Box>
      <Box display="flex" gap="24px" alignItems="flex-start">
        {showToggle && (
          <Box flexShrink={0} pt="8px">
            <PricingToggle
              value={billingPeriod}
              onChange={setSelectedPeriod}
              options={[
                { value: BILLING_PERIOD.MONTHLY, label: t('MONTHLY') },
                { value: BILLING_PERIOD.ANNUALLY, label: t('ANNUALLY') },
              ]}
            />
          </Box>
        )}

        <Box display="flex" gap="16px" flex={1}>
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              planId={plan.id}
              variantId={plan.variant?.id}
              name={plan.name}
              price={plan.price}
              period={plan.period}
              description={plan.description}
              currentPlanId={currentPlanId}
              currentVariantId={currentVariantId}
              subscriptionStatus={subscriptionStatus}
              highlighted={plan.highlighted}
              isCustom={plan.isCustom}
              isPrivate={plan.isPrivate}
              labels={{
                ...cardLabels,
                continueWith: t('CONTINUE_WITH', { plan: plan.name }),
              }}
              onSelect={onSelectPlan}
              // onDowngrade={onDowngrade}
              onLetsTalk={onLetsTalk}
            />
          ))}
        </Box>
      </Box>

      <PricingTable
        rows={comparisonRows}
        plans={plans}
        currency={currency}
        description={t('SERVICES_DESCRIPTION')}
      />
    </Box>
  );
};

export default PricingPlans;
